import Vue from 'vue';
import VueRouter from 'vue-router';

import JWTService from '@services/jwt.service';

import { SHORT_PROJECT_NAME } from '@config/application';

import store from '@store';
import { namespace as AuthNamespace, ActionTypes as AuthActionTypes } from '@store/modules/auth.types';
import {
  namespace as LocationsNamespace,
  MutationTypes as LocationsMutationTypes,
  ActionTypes as LocationsActionTypes,
} from '@store/modules/locations.types';

Vue.use(VueRouter);

const routes = [
  {
    path: '/login',
    name: 'login',
    components: {
      login: () => import('@components/AppLogin'),
    },
    meta: {
      title: 'Login',
    },
  },
  {
    path: '/403',
    name: 'forbidden',
    meta: {
      title: 'Forbidden',
    },
  },
  {
    path: '/404',
    name: 'error404',
    meta: {
      title: 'Page not found',
    },
  },
  {
    path: '*',
    redirect: '/404',
  },
];

// Automatic registration of routes from @modules
const requireModules = require.context(
  '@modules/',
  true,
  /^(((\.{1,2})|([a-zA-Z]+))\/)*([a-zA-Z]+\.)?(\b(routes)\b)?$/,
);
requireModules.keys().forEach((path) => {
  const route = requireModules(path).default;

  if (!route) {
    return;
  }

  if (Array.isArray(route)) {
    routes.push(...route);
  } else {
    routes.push(route);
  }
});

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

const initializeApp = async ({ name: routeName, query: routeQuery }) => {
  try {
    const generatedRouteQuery = await store.dispatch(
      `${AuthNamespace}/${AuthActionTypes.GENERATE_TOKEN_FROM_URL}`,
      { query: routeQuery },
    );

    await store.dispatch(
      `${LocationsNamespace}/${LocationsActionTypes.GET_LOCATIONS}`,
      { suffix: routeQuery.suffix },
    );

    generatedRouteQuery.suffix = generatedRouteQuery.suffix
      || store.state.locations.suffix
      || undefined;

    if (generatedRouteQuery.suffix) {
      store.commit(
        `${LocationsNamespace}/${LocationsMutationTypes.SET_SUFFIX}`,
        generatedRouteQuery.suffix,
      );
    }

    return Promise.resolve({
      query: routeName === 'login'
        ? {
          name: 'orders',
          query: {
            suffix: generatedRouteQuery.suffix,
            pageNum: '1',
          },
        }
        : generatedRouteQuery,
    });
  } catch (e) {
    return Promise.resolve({
      isSpecialRoute: true,
      routeName: e && e.status === 403 ? 'forbidden' : 'login',
    });
  }
};

let isInitAppLoading = true;

router.beforeEach(async (to, from, next) => {
  const { query, routeName, isSpecialRoute } = isInitAppLoading ? await initializeApp(to) : {};
  const { meta, name } = to;

  isInitAppLoading = false;

  if (query) {
    next({ ...to, query });
    return;
  }

  if (!JWTService.getToken()) {
    document.title = `${meta.title} | ${SHORT_PROJECT_NAME}`;
    next(name !== 'login' ? { name: 'login' } : undefined);
    return;
  }

  if (to.name === 'error404' || to.name === 'forbidden') {
    next();
    return;
  }

  if (name === 'login') {
    next({
      name: 'orders',
      query: {
        suffix: store.state.locations.suffix,
        pageNum: '1',
      },
    });
    return;
  }

  document.title = `${meta.title} | ${SHORT_PROJECT_NAME}`;

  if (isSpecialRoute) {
    next({ name: routeName });
    return;
  }

  // automatic location suffix setting
  if (to.meta.enableSetSuffix && to.meta.parent) {
    to.meta.parent.to.query.suffix = (() => store.state.locations.suffix)();
  }

  next();
});

export function createRouter() {
  return router;
}

export default router;
